import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  geoToberWallBuildUpApi,
  GeoToberWallBuildUpRequest,
} from "../api/wallSectionBuildUp";

export const GEO_TOBER_WALL_BUILD_UP_QUERY_KEYS = {
  detail: (tenderName: string, tenderVersion: number) =>
    ["geoToberWallBuildUp", tenderName, tenderVersion] as const,
};

export const useGetGeoToberWallBuildUp = (
  tenderName: string,
  tenderVersion: number,
  options?: {
    enabled?: boolean;
    staleTime?: number;
    refetchOnMount?: boolean | "always";
    refetchOnWindowFocus?: boolean;
  }
) => {
  const effectiveVersion = tenderVersion === 0 ? 1 : tenderVersion;
  return useQuery({
    queryKey: GEO_TOBER_WALL_BUILD_UP_QUERY_KEYS.detail(tenderName, effectiveVersion),
    queryFn: () => geoToberWallBuildUpApi.get(tenderName, effectiveVersion),
    enabled: (options?.enabled !== false) && !!tenderName && effectiveVersion >= 1,
    staleTime: options?.staleTime ?? 5 * 60 * 1000,
    refetchOnMount: options?.refetchOnMount,
    // See useGetRetainingWallsBuildUp's own comment (useRetainingWallsBuildUp.ts)
    // — "always refetch on mount" is not the same need as "refetch on window
    // focus"; defaulting this off stops every mounted instance of this query
    // from refetching on a plain tab-focus regain.
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });
};

export const usePostGeoToberWallBuildUp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: GeoToberWallBuildUpRequest) =>
      geoToberWallBuildUpApi.post(data),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: GEO_TOBER_WALL_BUILD_UP_QUERY_KEYS.detail(variables.tenderName, variables.tenderVersion),
      });
    },
    onError: (error: any) => {
      console.error("❌ Error saving geo tober wall build up:", error);
    },
  });
};
